import { computed, ref, onMounted, onBeforeUnmount, useHost, type ComputedRef, type Ref, type ToRefs } from "vue";
import type { ButtonProps } from "./button.types";
import { useSharedState } from "composables/useSharedState";
import { useSlotPresence } from "composables/useSlotPresence";
import { useIcons } from "composables/useIcons";
import { buttonClasses } from "./button.styles";
import { cardClasses } from "components/Card/card.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";

export interface UseButtonOptions {
  props: Readonly<ButtonProps>;
}

export interface UseButtonReturn {
  /** Reactive state from props */
  state: ToRefs<ButtonProps>;

  /** Computed classes object */
  classes: ComputedRef<ReturnType<typeof buttonClasses>>;

  /** Whether leading icon/slot should be shown */
  showLeading: ComputedRef<boolean>;

  /** Whether label/default slot should be shown */
  showLabel: ComputedRef<boolean>;

  /** Whether the default slot has meaningful (non-whitespace) content */
  hasDefaultSlot: Ref<boolean>;

  /** Whether trailing icon/slot should be shown */
  showTrailing: ComputedRef<boolean>;

  /** Leading icon name */
  leadingIconName: ComputedRef<string | undefined>;

  /** Trailing icon name */
  trailingIconName: ComputedRef<string | undefined>;

  /** The corrected size of the icons */
  iconSize: ComputedRef<string>;

  /** Whether button is in loading state */
  isLoading: Ref<boolean | undefined>;

  /** Whether button is disabled */
  isDisabled: Ref<boolean | undefined>;

  /** Resolved element tag to render */
  resolvedTag: ComputedRef<string>;

  /** Computed href for links */
  href: ComputedRef<string | undefined>;

  /** Computed target for links */
  target: ComputedRef<string | undefined>;

  /** Computed type for buttons */
  buttonType: ComputedRef<string | undefined>;

  /** Computed aria-disabled */
  ariaDisabled: ComputedRef<boolean | undefined>;

  /** Computed aria-busy */
  ariaBusy: ComputedRef<boolean | undefined>;

  /** Template ref to bind to the inner element */
  innerRef: Ref<HTMLElement | null>;
}

const iconSizeMap: Record<string, string> = {
  xl: "md",
  lg: "md",
  md: "sm",
  sm: "sm",
  xs: "xs",
};

const boolStr = (value: boolean): "true" | "false" => {
  return value ? "true" : "false";
};

const resolveButtonClassParams = (state: ToRefs<ButtonProps>) => {
  const card = cardClasses({
    color: state.color?.value,
    variant: state.variant?.value,
    size: state.size?.value,
  });
  const button = buttonClasses({
    variant: state.variant?.value,
    size: state.size?.value,
    square: boolStr(!!state.square?.value),
    block: boolStr(!!state.block?.value),
    disabled: boolStr(!!state.disabled?.value),
    loading: boolStr(!!state.loading?.value),
  });
  return { ...button, base: `${card} ${button.base}` };
};

const computeLinkAttrs = (state: ToRefs<ButtonProps>, isLink: ComputedRef<boolean>) => {
  const href = computed(() => (isLink.value ? state.to?.value : undefined));
  const target = computed(() => (isLink.value ? (state.target?.value ?? undefined) : undefined));
  const buttonType = computed(() => (isLink.value ? undefined : (state.type?.value ?? "button")));
  return { href, target, buttonType };
};

const setupHostKeydown = (host: Element, innerRef: Ref<HTMLElement | null>) => {
  const handler = (e: Event) => {
    const key = (e as KeyboardEvent).key;
    if (key === "Enter" || key === " ") {
      e.preventDefault();
      innerRef.value?.click();
    }
  };
  (host as HTMLElement)?.addEventListener("keydown", handler);
  return () => (host as HTMLElement)?.removeEventListener("keydown", handler);
};

export const useButton = (options: UseButtonOptions): UseButtonReturn => {
  const { props } = options;
  const state = useSharedState(props);
  const { isLeading, isTrailing, leadingIconName, trailingIconName } = useIcons(state);
  const {
    default: hasDefaultSlot,
    leading: hasLeadingSlot,
    trailing: hasTrailingSlot,
  } = useSlotPresence(["default", "leading", "trailing"]);

  const resolvedTag = computed(() => state.as?.value ?? (state.to?.value ? "a" : "button"));
  const isLink = computed(() => resolvedTag.value === "a");
  const showLeading = computed(() => isLeading.value || hasLeadingSlot.value);
  const showLabel = computed(() => hasDefaultSlot.value || !!state.label?.value);
  const showTrailing = computed(() => isTrailing.value || hasTrailingSlot.value);
  const classes = computed(() => mergeClassProp(resolveButtonClassParams(state), props.class));
  const iconSize = computed(() => iconSizeMap[state.size?.value ?? "md"] ?? "sm");
  const { href, target, buttonType } = computeLinkAttrs(state, isLink);
  const host = useHost();
  const innerRef = ref<HTMLElement | null>(null);
  let cleanupKeydown: (() => void) | null = null;
  onMounted(() => {
    cleanupKeydown = setupHostKeydown(host as Element, innerRef);
    if (host) (host as HTMLElement).focus = () => innerRef.value?.focus();
  });
  onBeforeUnmount(() => {
    cleanupKeydown?.();
  });

  return {
    state,
    classes,
    showLeading,
    showLabel,
    hasDefaultSlot,
    showTrailing,
    leadingIconName,
    trailingIconName,
    iconSize,
    isLoading: state.loading!,
    isDisabled: state.disabled!,
    resolvedTag,
    href,
    target,
    buttonType,
    ariaDisabled: computed(() => state.disabled?.value || state.loading?.value || undefined),
    ariaBusy: computed(() => state.loading?.value || undefined),
    innerRef,
  };
};
